import { useMcpTool } from 'webmcp-react'
import { uiStore } from 'shared/store/uiStore'
import { ContactsApi } from 'entities/Contacts'
import { TagsApi } from 'entities/Tags'
import { type Tag } from 'entities/Tags/model/Tag'
import { ContactsRoutes } from 'entities/ExportRouters'
import {
  searchContactsSchema,
  getContactSchema,
  openContactSchema,
  createContactSchema,
  updateContactSchema,
  tagContactSchema,
  listTagsSchema,
  createTagSchema,
} from 'entities/WebMcp/model/schemas'
import {
  serializeContactSummary,
  serializeContactDetail,
  serializeTag,
} from 'entities/WebMcp/lib/serializers'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'

/** Registers the Contacts WebMCP tools. Renders nothing. */
export const ContactsTools = () => {
  useMcpTool({
    name: 'search_contacts',
    description:
      'Search contacts by name, phone number, or email. Returns a compact list. Answers in chat only, so summarize the results there and include a [View all in Contacts](#contacts) link. Use this to resolve a contact before opening a conversation or a profile.',
    input: searchContactsSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ term, page, per_page }) => {
      try {
        const { data } = await ContactsApi.getContactsSearch({ term, page, per_page })
        return okResult({
          total: data.total,
          results: data.results.map(serializeContactSummary),
        })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'get_contact',
    description: 'Get full details for a single contact by id.',
    input: getContactSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ id }) => {
      try {
        const { data } = await ContactsApi.getContactById(id)
        return okResult(serializeContactDetail(data))
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'open_contact',
    description:
      "Open a specific contact's profile by id (from search_contacts or search_contacts_list). Navigates to the contact and opens their profile side panel.",
    input: openContactSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ contact_id }) => {
      try {
        // Deep-link: /contacts/:id; the Contacts page reads the id and opens the
        // profile panel (via ContactsPageStore.openContactById). changeRouteViaVue
        // resolves the correct bundle for the contacts route.
        uiStore.changeRouteViaVue({ path: `/${ContactsRoutes.root}/${contact_id}` })
        return okResult({ opened: true, contact_id })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'create_contact',
    description: 'Create a new contact. Requires a phone number in E.164 format.',
    input: createContactSchema,
    handler: async (params) => {
      try {
        const { data } = await ContactsApi.createContact(params)
        return okResult(serializeContactDetail(data))
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'update_contact',
    description: "Update an existing contact's name, email, or number.",
    input: updateContactSchema,
    handler: async ({ id, ...fields }) => {
      try {
        // Backend PUT requires `number`; only fetch to fill it when the caller
        // didn't supply one (the agent usually has it from a prior read tool).
        const number = fields.number ?? (await ContactsApi.getContactById(id)).data.number
        const { data } = await ContactsApi.updateContact(id, { ...fields, number })
        return okResult(serializeContactDetail(data))
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'tag_contact',
    description:
      "Apply one or more existing tags (by id + label) to a contact. Resolve tag names to ids with list_tags first, and create a tag only if it does not already exist. Needs the user's approval before it runs; tagging several contacts with the same tag in one reply is a single approval.",
    input: tagContactSchema,
    handler: async ({ contact_id, tags }) => {
      try {
        // The endpoint may return an empty/204 body; axios throws on non-2xx, so reaching
        // here means success. Echo the labels we applied rather than parsing the response.
        await ContactsApi.updateContactTags(contact_id, tags as unknown as Tag[])
        return okResult({ contact_id, applied: tags.map((t) => t.label) })
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'list_tags',
    description:
      'List all tags in the workspace (id + label, and contacts_count when available). Use to resolve a tag name to its id before tag_contact, and to check a tag is not empty before using it as a broadcast audience.',
    input: listTagsSchema,
    annotations: { readOnlyHint: true },
    handler: async () => {
      try {
        const { data } = await TagsApi.getAllTags()
        return okResult(data.data.map(serializeTag))
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'create_tag',
    description:
      'Create a new tag by label and return its id. Use when a desired tag does not exist yet.',
    input: createTagSchema,
    handler: async ({ label }) => {
      try {
        const { data } = await TagsApi.createTag({ label })
        return okResult(serializeTag(data))
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  return null
}
